import { WelcomeForm } from '@features/onboarding/components/1-welcome-form/WelcomeForm';
import { ApplicantsForm } from '@features/onboarding/components/2-applicants-form/ApplicantsForm';
import { MedicalPlansForm } from '@features/onboarding/components/3-medical-plans-form/MedicalPlansForm';
import { DentalAndVisionForm } from '@features/onboarding/components/4-dental-and-vision-form/DentalAndVisionForm';
// import { SummaryForm } from '@features/onboarding/components/5-beyond-health-form/SummaryForm';
import { HealthQuestionForm } from '@features/onboarding/components/6-health-question-form/HealthQuestionForm';
import { EstimateResult } from '@features/onboarding/components/7-finish/components/estimate-result';
import { Finish } from '@features/onboarding/components/7-finish/Finish';
import {
  FormStepSections,
  FormStepID,
} from '@features/onboarding/enums/formSections.enum';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useApplicationStore } from 'src/store/application';
import { useGeneralStore } from 'src/store';
import { useFinalSubmissionStore } from 'src/store/final-submission';
// import { useMemo } from 'react';
import { useInsuranceStore } from 'src/store/insurance';

export interface FormStep {
  id: string;
  number: number;
  title: string;
  sectionName: string;
  description?: string;
  subscription?: string;
  disabled?: boolean;
  image?: string;
  showSummary?: boolean;
  Component: React.ComponentType<FormStepProps>;
  defaultStep?: number;
}

export const useFormSteps: () => FormStep[] = () => {
  const { data, phqSubmitted } = useOnboardingStore();
  const { acceptedTier, isMainPlanRejected, phqUnderReviewAt, phqAcceptedAt } = useApplicationStore();
  const medicalPlan = data?.medicalPlan;
  const medicalPlanRejected = data?.medicalPlanRejected;
  const { signedAt } = useApplicationStore();
  const { siteConfig } = useGeneralStore();
  const { underwritingResult } = useFinalSubmissionStore();
  const plans = useInsuranceStore((state) => state.plans);

  const availablePlans = plans.filter((item) =>
    siteConfig?.plans?.includes(item.id)
  );
  const availbaleCategories = availablePlans.map((item) => item.category);
  const hasHealthPlanRejection = Boolean(
    isMainPlanRejected || medicalPlanRejected
  );
  // console.log('available categories', [...new Set(availbaleCategories)]);

  const steps: FormStep[] = [
    {
      id: FormStepID.Welcome,
      number: 1,
      showSummary: false,
      sectionName: FormStepSections.Welcome,
      title: siteConfig?.welcomeTitle || '',
      description: siteConfig?.welcomeDescription.replace(/\\n/g, '\n') || '',
      Component: WelcomeForm,
    },
    {
      id: FormStepID.Dependents,
      number: 2,
      showSummary: false,
      sectionName: FormStepSections.Dependents,
      title: 'A little information about you and your dependents.',
      description:
        'On this page, you’ll enter details about yourself and any dependents you would like to be covered under your benefits. Once added, you can include or remove dependents from any of the plans you choose.',
      Component: ApplicantsForm,
    },
  ];

  // PHQ comes after Dependents (step 3) if user has selected a medical plan
  if (!acceptedTier && !isMainPlanRejected && !phqAcceptedAt) {
    steps.push({
      id: FormStepID.PHQ,
      number: 3,
      sectionName: FormStepSections.HealthQuestion,
      title: 'First up: some quick health questions.',
      description:
        'This part is what we call the Personal Health Questionnaire (PHQ). Give your best, honest answers; We’ll use them to find the right plans and your exact price. Fast. Private. Done in minutes.',
      Component: phqUnderReviewAt ? EstimateResult : HealthQuestionForm,
    });
  }

  // EstimateResult step - shown after PHQ to display underwriting results
  // Always add it so structure remains stable even after acceptedTier is set
  steps.push({
    id: FormStepID.Approved,
    number: steps.length + 1,
    sectionName: FormStepSections.EstimateResult,
    title: 'Underwriting Result.',
    description: '',
    Component: EstimateResult,
  });

  // Health Plans step - shown after PHQ if user has been accepted
  // Skip this step if user was rejected by underwriting
  if (
    underwritingResult?.status !== 'rejected' &&
    !hasHealthPlanRejection &&
    availbaleCategories.includes('Major Medical')
  ) {
    steps.push({
      id: FormStepID.HealthPlans,
      number: steps.length + 1,
      sectionName: FormStepSections.Medical,
      title: 'Health Plans.',
      description: `Welcome to your Plans page! Choosing the right plan is one of the most important steps you can take for your health and your wallet—and we're here to make it easy. We offer a wide range of plans to fit almost every budget, whether you're looking for low cost coverage or something more comprehensive. Take your time exploring your options, and remember: if you have questions, want to compare plans, or run through any "what if" scenarios, our friendly AI assistant Alia is just a click away. She's here to guide you every step of the way!`,
      subscription:
        "<strong>A quick note about premium quotes</strong><br />Your final quote may be different than the quotes shown on this page. Once you complete the application, we'll provide a personalized quote with your exact monthly premium.",
      Component: MedicalPlansForm,
    });
  }

  // Dental and Vision plans
  steps.push({
    id: FormStepID.VisionPlans,
    number: steps.length + 1,
    sectionName: FormStepSections.Dental,
    title: 'Dental plans.',
    description:
      "Taking care of your smile is more than just brushing and flossing—it's about having the right coverage when you need it. That's why we offer two dental plans designed to fit different needs and budgets. Whether you're looking for essential coverage for routine checkups or more comprehensive protection that includes major procedures, we've got you covered. A good dental plan can help you stay on top of your oral health while avoiding unexpected costs.",
    Component: DentalAndVisionForm,
  });

  if (medicalPlan) {
    steps.push({
      id: FormStepID.CoverageStartDate,
      number: steps.length + 1,
      sectionName: FormStepSections.CoverageStartDate,
      title: 'Choose your effective date.',
      description:
        "Effective date is just a fancy word for when you'd like your coverage to start. Your effective date always starts on the first of the month. We give you 2 options.",
      Component: HealthQuestionForm,
      defaultStep: 5,
    });
  }

  // Finish step
  steps.push({
    id: 'Finish',
    number: steps.length + 1,
    sectionName: FormStepSections.Finish,
    title: 'Congratulations!',
    description:
      "Congratulations! You've been approved for your medical insurance, and your final premium is locked in —great job taking this important step toward protecting your health. If you ever want to make changes to the plans you selected, no problem!",
    Component: Finish,
  });

  return steps.filter((e) => {
    // Remove PHQ if already signed
    if (e.id === FormStepID.PHQ && signedAt) {
      return false;
    }
    // Remove EstimateResult if PHQ was skipped (already signed) OR not yet submitted
    if (e.id === FormStepID.Approved && (signedAt || !phqSubmitted)) {
      return false;
    }
    return true;
  });
};
